import { useState, useEffect, useRef } from "react";
import { HelpCircle, X } from "lucide-react";

/**
 * Patient-facing "?" tooltip — plain-language explanation in a small popover.
 * Distinct from the staff `HelpLink`:
 *   - No event dispatch / no routing to the staff manual.
 *   - Self-contained: copy is passed in via `title` + `body` props.
 *   - Closes on outside click, Escape, or the X button.
 *
 * Designed for the patient portal where we can't link to staff training but
 * patients still ask CS the same handful of questions ("what's this number for?",
 * "where did my receipt go?", etc.). Each anchor inlines its own copy so we
 * don't grow a separate content collection just for this.
 */
export default function PatientHelpTooltip({
  title,
  body,
  size = 14,
  className = "",
  align = "left",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={wrapperRef} className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        title="What's this?"
        aria-label="Help — open explanation"
        aria-expanded={open}
        className="inline-flex items-center justify-center text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors cursor-pointer"
        data-testid={testId ? `patient-help-${testId}` : "patient-help"}
      >
        <HelpCircle style={{ width: size, height: size }} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={title}
          className={`absolute top-full mt-2 z-30 w-72 sm:w-80 rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-white dark:bg-slate-900 shadow-xl p-3.5 text-left ${align === "right" ? "right-0" : "left-0"}`}
          data-testid={testId ? `patient-help-popover-${testId}` : "patient-help-popover"}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <p className="text-sm font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {title}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700"
              aria-label="Close"
              data-testid={testId ? `patient-help-close-${testId}` : "patient-help-close"}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed space-y-1.5">
            {typeof body === "string" ? <p>{body}</p> : body}
          </div>
        </div>
      )}
    </span>
  );
}
